import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получает список задач с применением фильтров.
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные задач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getList = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/tasks${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список всех задач без пагинации
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные задач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTasksOptions = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = new URLSearchParams(cleanedFilters).toString();

  try {
    const response = await fetch(
      `/api/tasks${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const exportTasks = async (item, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/tasks/export${isAdmin ? "/admin" : ""}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(item),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при выгрузке задач. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.file;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка запланированных задач
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные задач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getRecurringItems = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/recurring-tasks/future?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.tasks;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает задачу по UUID.
 * @param {string} uuid - UUID задачи.
 * @returns {Promise<Object>} - Данные задачи.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getItem = async (uuid, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task${isAdmin ? "/admin" : ""}?uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.task;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новую задачу.
 * @param {Object} item - Данные задачи.
 * @returns {Promise<Object>} - Созданная задача.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/task", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.task;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новую задачу из таблицы.
 * @param {Object} item - Данные задачи.
 * @returns {Promise<Object>} - Созданная задача.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createItemFromTable = async (item) => {
  try {
    const response = await fetch("/api/task/from-table", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.tasks;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновляет задачи, созданные из строк табличного ресурса.
 * @param {Object} item - Данные синхронизации.
 * @returns {Promise<Object>} - Результат синхронизации задач.
 * @throws {Error} - Ошибка при обновлении данных.
 */
export const syncItemsFromTable = async (item) => {
  try {
    const response = await fetch("/api/task/from-table/update", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении задач. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновляет существующую задачу.
 * @param {Object} item - Данные задачи.
 * @returns {Promise<Object>} - Обновленная задача.
 * @throws {Error} - Ошибка при обновлении данных.
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/task", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.task;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаляет задачу.
 * @param {Object} item - Данные задачи.
 * @returns {Promise<Object>} - Удаленная задача.
 * @throws {Error} - Ошибка при удалении данных.
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/task", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.task;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает подзадачи по UUID задачи.
 * @param {string} uuid - UUID задачи.
 * @param {boolean} isAdmin - Использовать административный маршрут.
 * @param {boolean} includeDeleted - Включать ли удаленные задачи.
 * @returns {Promise<Object>} - Данные подзадач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getChildren = async (
  uuid,
  isAdmin = false,
  includeDeleted = false,
) => {
  const queryString = new URLSearchParams({
    uuid,
    include_deleted: includeDeleted,
  }).toString();

  try {
    const response = await fetch(
      `/api/subtasks${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.subtasks;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новый статус задачи.
 * @param {Object} status - Данные статуса.
 * @returns {Promise<Object>} - Созданный статус.
 * @throws {Error} - Ошибка при создании статуса.
 */
export const createStatus = async (status) => {
  try {
    const response = await fetch("/api/status", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(status),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании статуса. ${data.error || "Попробуйте еще раз"}`,
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новый приоритет задачи.
 * @param {Object} priority - Данные приоритета.
 * @returns {Promise<Object>} - Созданный приоритет.
 * @throws {Error} - Ошибка при создании приоритета.
 */
export const createPriority = async (priority) => {
  try {
    const response = await fetch("/api/importance", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(priority),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании приоритета. ${data.error || "Попробуйте еще раз"}`,
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепить файл к задаче
 * @param {Object} item - Данные файла.
 * @returns {Promise<Object>} - Созданный файл.
 * @throws {Error} - Ошибка при создании данных.
 */
export const addFile = async (item) => {
  try {
    const response = await fetch("/api/task/file", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепить ярлык к задаче
 * @param {Object} item - Данные ярлыка.
 * @returns {Promise<Object>} - Созданный ярлык.
 * @throws {Error} - Ошибка при создании данных.
 */
export const addFileLink = async (item) => {
  try {
    const response = await fetch("/api/task/shortcut", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить файлы задачи
 * @param {Object} params - {uuid } uuid - id задачи
 * @returns {Promise<Object>} - Содержит файлы задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTaskFiles = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task/files${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const TASK_COMMENT_REACTION_EMOJIS = [
  "👍",
  "👎",
  "😄",
  "🎉",
  "😟",
  "❤️",
  "🚀",
  "👀",
  "🔥",
  "👌",
  "🤔",
  "✅",
];

export const TASK_COMMENT_REACTIONS = TASK_COMMENT_REACTION_EMOJIS.map(
  (emoji) => ({
    code: emoji,
    emoji,
    name: emoji,
  }),
);

const getFileDownloadUrl = (file) =>
  file.url || file.src || `/api/file/download?uuid=${file.uuid}`;

const normalizeTaskCommentFilesPayload = (attachments = []) => {
  const payload = {
    files: [],
    file_uuids: [],
  };

  attachments.forEach((attachment) => {
    if (!attachment?.uuid) return;

    if (attachment.isSystem || attachment.shortcut_uuid) {
      payload.file_uuids.push(attachment.file_uuid || attachment.uuid);
      return;
    }

    const file = { ...attachment };
    delete file.rawFile;
    delete file.url;
    delete file.src;
    delete file.preview_url;
    delete file.previewSrc;
    delete file.local_preview_url;

    payload.files.push(file);
  });

  payload.files = payload.files.filter(
    (file, index, list) => list.findIndex(({ uuid }) => uuid === file.uuid) === index,
  );
  payload.file_uuids = [...new Set(payload.file_uuids)];

  return payload;
};

/**
 * Получает комментарии задачи.
 *
 * @param {Object} params - Параметры запроса.
 * @param {string} params.task_uuid - UUID задачи.
 * @returns {Promise<Array>} - Список комментариев задачи.
 * @throws {Error} - Ошибка при получении комментариев.
 */
export const getTaskComments = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(`/api/task/comments?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении комментариев. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.comments || data.data || data.items || [];
  } catch (err) {
    throw err;
  }
};

/**
 * Создаёт комментарий к задаче.
 * Контракт бэка: task_uuid, body, опционально quoted_comment_uuid.
 *
 * @param {Object} item - Данные комментария.
 * @param {string} item.task_uuid - UUID задачи.
 * @param {string} item.body - Markdown-текст комментария.
 * @returns {Promise<Object>} - Созданный комментарий.
 * @throws {Error} - Ошибка при добавлении комментария.
 */
export const addTaskComment = async (item) => {
  try {
    const filesPayload = normalizeTaskCommentFilesPayload(item.attachments);
    const payload = {
      task_uuid: item.task_uuid,
      body: item.body,
      quoted_comment_uuid: item.quoted_comment_uuid,
      ...(filesPayload.files.length ? { files: filesPayload.files } : {}),
      ...(filesPayload.file_uuids.length
        ? { file_uuids: filesPayload.file_uuids }
        : {}),
    };

    const response = await fetch("/api/task/comment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении комментария. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.comment || data.item || data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновляет текст комментария.
 * Бэк сам запишет изменение в историю задачи; фронт передаёт uuid комментария и новый body.
 *
 * @param {Object} item - Данные обновления.
 * @param {string} item.uuid - UUID комментария.
 * @param {string} item.body - Новый Markdown-текст.
 * @returns {Promise<Object>} - Обновлённый комментарий.
 * @throws {Error} - Ошибка при обновлении комментария.
 */
export const updateTaskComment = async (item) => {
  try {
    const response = await fetch("/api/task/comment", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uuid: item.uuid,
        body: item.body,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении комментария. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.comment || data.item || data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаляет комментарий из задачи.
 * Пока вложений нет, фронт передаёт только uuid; удаление связанных файлов позже останется на бэке.
 *
 * @param {Object} item - Данные удаления.
 * @param {string} item.uuid - UUID комментария.
 * @returns {Promise<Object>} - Удалённый комментарий или результат удаления.
 * @throws {Error} - Ошибка при удалении комментария.
 */
export const deleteTaskComment = async (item) => {
  try {
    const response = await fetch("/api/task/comment", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении комментария. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.comment || data.item || data;
  } catch (err) {
    throw err;
  }
};

/**
 * Переключает реакцию текущего пользователя на комментарии.
 * Контракт бэка: comment_uuid, emoji; если такая реакция пользователя уже есть, бэк снимает её.
 * В ответе удобно возвращать обновлённый список reactions для комментария или комментарий целиком.
 *
 * @param {Object} item - Данные реакции.
 * @param {string} item.comment_uuid - UUID комментария.
 * @param {string} item.emoji - Эмодзи реакции из TASK_COMMENT_REACTION_EMOJIS.
 * @returns {Promise<Object>} - Обновлённый комментарий или список реакций.
 * @throws {Error} - Ошибка при переключении реакции.
 */
export const toggleTaskCommentReaction = async (item) => {
  try {
    const response = await fetch(
      "/api/task/comment/reaction",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          comment_uuid: item.comment_uuid,
          emoji: item.emoji,
        }),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении реакции. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.comment || data.reactions || data;
  } catch (err) {
    throw err;
  }
};

export const addTaskCommentFiles = async (item) => {
  try {
    const filesPayload = normalizeTaskCommentFilesPayload(item.attachments);
    const payload = {
      comment_uuid: item.comment_uuid,
      ...(filesPayload.files.length ? { files: filesPayload.files } : {}),
      ...(filesPayload.file_uuids.length
        ? { file_uuids: filesPayload.file_uuids }
        : {}),
    };

    const response = await fetch("/api/task/comment/file", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        "Ошибка при добавлении файлов к комментарию. " +
          (data.error || "Попробуйте снова"),
      );
    }

    return data.comment || data.item || data;
  } catch (err) {
    throw err;
  }
};

/**
 * Загружает изображение для комментария.
 * В реальном режиме повторяет существующий паттерн проекта: POST /api/file/upload с FormData и берёт первый файл из response.files.
 *
 * @param {File} file - Изображение из input/paste/drop.
 * @returns {Promise<Object>} - File-like объект: uuid, original_name, extension, url.
 * @throws {Error} - Ошибка при загрузке файла.
 */
export const uploadTaskCommentImage = async (file, meta = {}) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("task_uuid", meta.task_uuid || "");
    formData.append("name", meta.name || "");
    formData.append("description", meta.description || "");

    const response = await fetch("/api/file/upload", {
      method: "POST",
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при загрузке изображения. ${data.error || "Попробуйте снова"}`,
      );
    }

    const uploadedFile = data.file || data.attachment || data.files?.[0];

    if (!uploadedFile) {
      throw new Error("Ошибка при загрузке изображения. Файл не найден в ответе сервера");
    }

    return {
      ...uploadedFile,
      name: meta.name || uploadedFile.name,
      description: meta.description || uploadedFile.description,
      task_uuid: meta.task_uuid,
      url: getFileDownloadUrl(uploadedFile),
    };
  } catch (err) {
    throw err;
  }
};

/**
 * Создать копию задачи
 * @param {Object} item - Данные.
 * @returns {Promise<Object>} - Транзакция копирования.
 * @throws {Error} - Ошибка при копировании данных.
 */
export const copyTask = async (item) => {
  try {
    const response = await fetch("/api/task/copy", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при копировании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение роли пользователя в задаче
 * @param {string} uuid - UUID задачи
 * @returns {Promise<Object>} - Роль пользователя в задаче
 * @throws {Error} - Ошибка при получении данных.
 */
export const getUserRoles = async (uuid) => {
  try {
    const response = await fetch(`/api/tasks/roles?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.roles;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка объектов, которые можно прикрепить к задаче
 * @param {Object} params - {task_uuid, definition_uuid} task_uuid - id задачи, definition_uuid - id описателя
 * @returns {Promise<Object>} - Содержит список объектов, которые можно прикрепить к задаче
 * @throws {Error} - Ошибка при получении данных.
 */
export const getAttachableObjects = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task/attachable-objects${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка связанных объектов, которые можно прикрепить к задаче
 * @param {Object} params - { task_uuid, parent_definition_uuid, parent_object_uuid, field_uuid } - task_uuid - id задачи, parent_definition_uuid - id описателя родительского объекта, parent_object_uuid - id родительского объекта, field_uuid - field.uuid табличного поля
 * @returns {Promise<Object>} - Содержит список связанных объектов, которые можно прикрепить к задаче
 * @throws {Error} - Ошибка при получении данных.
 */
export const getNestedCandidates = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task/attachable-objects/nested-candidates${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить связанные ресурсы задачи
 * @param {Object} params - {uuid } uuid - id задачи
 * @returns {Promise<Object>} - Содержит связанные ресурсы задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTaskObjects = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task/resource-types${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data || [];
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепление ресурсов к задаче
 * @param {Object} params - type: { task_uuid, definition_uuid, quantity, status, comment },
                            objects: [{ object_uuid, quantity }]
 * @returns {Promise<Object>} - Содержит список прикрепленных ресурсов
 * @throws {Error} - Ошибка при прикреплении ресурсов.
 */
export const attachResources = async (method, params, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task/resource-type${isAdmin ? "/admin" : ""}`,
      {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при прикреплении ресурсов. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление объекта из задачи
 * @param {Object} params - {task_object_uuid} task_object_uuid - id объекта
 * @returns {Promise<Object>} - Содержит список прикрепленных объектов
 * @throws {Error} - Ошибка при удалении объекта.
 */
export const deleteAttachedObject = async (params, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task/resource-type/object${isAdmin ? "/admin" : ""}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении объекта. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление типа объекта из задачи
 * @param {Object} params - {task_uuid, object_uuid} task_uuid - id задачи, object_uuid - id типа объекта
 * @returns {Promise<Object>} - Содержит список прикрепленных объектов
 * @throws {Error} - Ошибка при удалении типа объекта.
 */
export const deleteAttachedObjectType = async (params, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task/resource-type${isAdmin ? "/admin" : ""}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении типа объекта. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение чеклиста задачи
 * @param {string} uuid - UUID задачи
 * @returns {Promise<Object>} - Чеклист задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getChecklist = async (uuid) => {
  try {
    const response = await fetch(`/api/task/checklist?task_uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.checklist;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавление элемента в чеклист задачи
 * @param {Object} item - Данные элемента чеклиста {task_uuid, title, idx}
 * @returns {Promise<Object>} - Добавленный элемент чеклиста
 * @throws {Error} - Ошибка при добавлении элемента.
 */
export const addChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task/checklist/item`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении элемента в чек-лист. ${
          data.error || "Попробуйте снова"
        }`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление элемента чеклиста задачи
 * @param {Object} item - Данные элемента чеклиста {uuid, title, idx}
 * @returns {Promise<Object>} - Обновленный элемент чеклиста
 * @throws {Error} - Ошибка при обновлении элемента.
 */
export const updateChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task/checklist/item`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении элемента чек-листа. ${
          data.error || "Попробуйте снова"
        }`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление элемента из чеклиста задачи
 * @param {Object} uuid - UUID элемента чеклиста
 * @returns {Promise<Object>} - Удаленный элемент чеклиста
 */
export const deleteChecklistItem = async (uuid) => {
  try {
    const response = await fetch(`/api/task/checklist/item`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uuid }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении элемента чек-листа. ${
          data.error || "Попробуйте снова"
        }`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Чекнуть галочку
 * @param {Object} item - Данные элемента чеклиста {uuid, checked}
 * @returns {Promise<Object>} - Обновленный элемент чеклиста
 * @throws {Error} - Ошибка при обновлении элемента.
 */
export const checkChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task/checklist/item/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении элемента чек-листа. ${
          data.error || "Попробуйте снова"
        }`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление чеклиста задачи
 * @param {Object} item - Данные задачи {task_uuid}
 * @returns {Promise<Object>} - Удаленный чеклист
 */
export const deleteChecklist = async (task_uuid) => {
  try {
    const response = await fetch(`/api/task/checklist`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ task_uuid }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении чек-листа. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение правил повторения задачи
 * @param {string} uuid - UUID задачи
 * @returns {Promise<Object>} - Правила повторения задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getRepeatRules = async (uuid) => {
  try {
    const response = await fetch(`/api/task/recurrence?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении правил повторения задачи. ${
          data.error || "Попробуйте снова"
        }`,
      );
    }

    return data.rules;
  } catch (err) {
    throw err;
  }
};
